import {
    Pagination,
    PaginationItem,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material"
import {
    StyledTable,
    StyledTableCell,
    StyledTableRow,
} from "components/tables/StyledTable.tsx"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { ListRfidInfo, RfidInfo, RfidsTableColumnsType } from "types/rfid"
import ActiveCardIcon from "assets/svg/ActivateCard.svg"
import DeactiveCardIcon from "assets/svg/DeactivateCard.svg"
import { SortOrder } from "types/sort.ts"
import { RfidHeaderCell } from "layouts/rfid/rfid-dashboard/cells.tsx"
import { UseRfidContext } from "layouts/rfid/context/provider.tsx"
import { OreveButton } from "components/button/OreveButton.tsx"
import DoDisturbAltOutlinedIcon from "@mui/icons-material/DoDisturbAltOutlined"
import ReplayIcon from "@mui/icons-material/Replay"
import {
    rfidColumnsOrder,
    rfidsColumns,
    RfidStatus,
} from "layouts/rfid/utils/const.ts"

export const RfidTable = ({
    rows,
    sortOrder,
    orderBy,
    setSortOrder,
    setOrderBy,
}: {
    rows: ListRfidInfo
    sortOrder: SortOrder
    orderBy: keyof RfidsTableColumnsType
    setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>
    setOrderBy: React.Dispatch<
        React.SetStateAction<keyof RfidsTableColumnsType>
    >
}) => {
    const [pageNumber, setPageNumber] = useState(1)
    const [numberOfPages, setNumberOfPages] = useState(0)
    const rowsPerPage = 10
    const {
        setCurrentCardSelected,
        stateDisableCard,
        stateReactivateCard,
        setTableButton,
    } = UseRfidContext()

    useEffect(() => {
        setNumberOfPages(Math.ceil(rows.length / rowsPerPage))
        setPageNumber(1)
    }, [rows.length])

    const currentRfidsRows = useMemo(() => {
        return rows.slice(
            rowsPerPage * (pageNumber - 1),
            rowsPerPage * pageNumber
        )
    }, [rows, pageNumber])

    const handleChangePage = useCallback(
        (_event: React.ChangeEvent<unknown>, page: number) => {
            setPageNumber(page)
            setCurrentCardSelected(null)
        },
        []
    )

    return (
        <>
            <TableContainer sx={{ borderRadius: "10px 10px 0 0" }}>
                <StyledTable aria-label="customized table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell sx={{ width: "9%" }} />
                            {rfidColumnsOrder.map((col) => {
                                return (
                                    <RfidHeaderCell
                                        key={col}
                                        keyRfid={col}
                                        title={rfidsColumns[col].title}
                                        width={rfidsColumns[col].width}
                                        sortOrder={sortOrder}
                                        orderBy={orderBy}
                                        setSortOrder={setSortOrder}
                                        setOrderBy={setOrderBy}
                                        isSortable={
                                            rfidsColumns[col].isSortable
                                        }
                                    />
                                )
                            })}
                            <StyledTableCell sx={{ width: "20%" }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currentRfidsRows.map((item: RfidInfo) => (
                            <StyledTableRow key={item.id}>
                                <StyledTableCell
                                    sx={{
                                        opacity:
                                            item.status === RfidStatus.Active
                                                ? 1
                                                : 0.5,
                                    }}
                                    style={{ borderRight: "none" }}
                                >
                                    {item.status === RfidStatus.Active ? (
                                        <img
                                            alt="Active RFID card"
                                            src={ActiveCardIcon}
                                        />
                                    ) : (
                                        <img
                                            alt="Inactive RFID card"
                                            src={DeactiveCardIcon}
                                        />
                                    )}
                                </StyledTableCell>
                                {rfidColumnsOrder.map((col, colIndex) => {
                                    const columnKey =
                                        col as keyof RfidsTableColumnsType
                                    const CellComponent =
                                        rfidsColumns[columnKey]?.cellComponent

                                    const FallbackCell: React.FC<{
                                        rfid: RfidInfo
                                    }> = ({ rfid }) => {
                                        switch (columnKey) {
                                            case "rfid_number":
                                                return <>{rfid.rfidNumber}</>
                                            case "driver":
                                                return <>{rfid.cardHolder}</>
                                            case "status":
                                                return (
                                                    <>
                                                        {rfid.status ===
                                                        RfidStatus.Active
                                                            ? "Active"
                                                            : "Désactivée"}
                                                    </>
                                                )
                                            default:
                                                return null
                                        }
                                    }

                                    const RenderCell =
                                        CellComponent ?? FallbackCell

                                    return (
                                        <StyledTableCell
                                            key={columnKey}
                                            sx={{
                                                opacity:
                                                    colIndex === 0
                                                        ? 1
                                                        : item.status ===
                                                            RfidStatus.Active
                                                          ? 1
                                                          : 0.5,
                                            }}
                                        >
                                            <RenderCell rfid={item} />
                                        </StyledTableCell>
                                    )
                                })}
                                <StyledTableCell>
                                    {item.status === RfidStatus.Active ? (
                                        <OreveButton
                                            variant="text"
                                            value="Désactiver"
                                            removeMargin={true}
                                            icon={DoDisturbAltOutlinedIcon}
                                            onClick={() => {
                                                stateDisableCard()
                                                setCurrentCardSelected(item)
                                                setTableButton(true)
                                            }}
                                            sx={{
                                                textDecoration: "none",
                                                m: "0rem",
                                            }}
                                        />
                                    ) : (
                                        <OreveButton
                                            data-cy="row__rfid--reactivate"
                                            variant="text"
                                            value="Réactiver"
                                            removeMargin={true}
                                            icon={ReplayIcon}
                                            onClick={() => {
                                                stateReactivateCard()
                                                setCurrentCardSelected(item)
                                                setTableButton(true)
                                            }}
                                            sx={{
                                                textDecoration: "none",
                                                m: "0rem",
                                            }}
                                        />
                                    )}
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </StyledTable>
            </TableContainer>
            <Pagination
                data-testid="Pagination"
                color="primary"
                count={numberOfPages}
                page={pageNumber}
                onChange={handleChangePage}
                renderItem={(item) => {
                    if (item.type === "previous") {
                        return (
                            <PaginationItem
                                {...item}
                                data-cy="cards__pagination--prev"
                            />
                        )
                    }
                    if (item.type === "next") {
                        return (
                            <PaginationItem
                                {...item}
                                data-cy="cards__pagination--next"
                            />
                        )
                    }
                    return <PaginationItem {...item} />
                }}
                sx={{
                    mx: "auto",
                }}
            />
        </>
    )
}
